import { browserStorage, createAccount, createAuth, deviceLabel, hasBackend, readEntryIntent } from "@hachi/api";
import { LEGACY_KEYS } from "@hachi/core";
import type { TrialPlan } from "@hachi/api";

/**
 * The one backend this build talks to. Set at build time; previews point at the preview
 * Supabase project. A build without settings has no backend and opens straight into the app.
 */
const cfg = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? "",
  supabaseKey: import.meta.env.VITE_SUPABASE_KEY ?? "",
  emailSignIn: true,
};

export const backendReady = hasBackend(cfg);
export const supabaseConfig = { supabaseUrl: cfg.supabaseUrl, supabaseKey: cfg.supabaseKey };
export const store = browserStorage();

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const auth = createAuth(cfg, { store });

export const accountCall = createAccount({
  store,
  auth,
  label: () => deviceLabel({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints, standalone: isStandalone() }),
});

/** Where Google returns: this page's address without query or hash (Supabase must allow it). */
export const returnAddress = () => location.origin + location.pathname;

/**
 * Tells hachi540i.com whether this browser is signed in (a flag cookie, no account data),
 * so the website shows "Garage" instead of "Try it free" (`syncInCookie`).
 */
export function syncSignedInCookie(on: boolean) {
  if (!/(^|\.)hachi540i\.com$/.test(location.hostname)) return;
  try {
    document.cookie = `${on ? "hg_in=1; Max-Age=31536000" : "hg_in=; Max-Age=0"}; Domain=hachi540i.com; Path=/; SameSite=Lax; Secure`;
  } catch {
    // Cookies blocked: the website just keeps showing "Try it free".
  }
}

const WANT_TRIAL = "hg-want-trial", FROM_CHECKOUT = "hg-from-checkout";
const session = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* blocked */ } },
  del: (k: string) => { try { sessionStorage.removeItem(k); } catch { /* blocked */ } },
};

/**
 * Remembers what the website asked for (`?trial=`, `?demo=1`, `?from=checkout`) for this visit and
 * cleans the address. Runs once at load, before the router reads it. Back from checkout, a person
 * returns to the page they were on.
 */
function captureEntry() {
  const intent = readEntryIntent(location.search);
  let hash = location.hash;
  if (intent.trial && !(new URLSearchParams(location.search).has("demo") && session.get(WANT_TRIAL))) session.set(WANT_TRIAL, intent.trial);
  if (intent.fromCheckout) {
    session.set(FROM_CHECKOUT, "1");
    const last = store.getItem(LEGACY_KEYS.lastView);
    if (last && (!location.hash || location.hash === "#/") && /^#\/[\w/?=&%.-]*$/.test(last)) hash = last;
  }
  if (intent.rest !== location.search || hash !== location.hash) history.replaceState(null, "", location.pathname + intent.rest + hash);
}
captureEntry();

export const wantTrial = () => (session.get(WANT_TRIAL) as TrialPlan | null) ?? "";
export const clearWantTrial = () => session.del(WANT_TRIAL);
export const fromCheckout = () => session.get(FROM_CHECKOUT) === "1";
export const clearFromCheckout = () => session.del(FROM_CHECKOUT);
